import type {Item} from '@PosterWhiteboard/items/item/item.class';
import type {ShowItemLoadingOtps} from '@PosterWhiteboard/items/item/item.types';
import {ItemLoadingProgressType} from '@PosterWhiteboard/items/item/item.types';
import {initFakeProgress} from '@Libraries/fake-loading.library';
import {updateItemLoadingState} from '@Components/poster-editor/poster-editor-reducer';
import type {ItemLoadingState} from '@Components/poster-editor/poster-editor-reducer';

interface ItemLoadingInternalState {
  currentProgress: number;
  loadingOpts?: ShowItemLoadingOtps;
  progressInterval?: NodeJS.Timeout;
  isVisible: boolean;
}

const itemLoadingStateMap = new Map<string, ItemLoadingInternalState>();

const getStateKey = (item: Item): string => {
  return `${item.page.hashedID}:${item.uid}`;
};

const getItemState = (item: Item): ItemLoadingInternalState | undefined => {
  return itemLoadingStateMap.get(getStateKey(item));
};

const setItemState = (item: Item, state: ItemLoadingInternalState): void => {
  itemLoadingStateMap.set(getStateKey(item), state);
};

const removeItemState = (item: Item): void => {
  itemLoadingStateMap.delete(getStateKey(item));
};

const toReduxLoadingState = (state: ItemLoadingInternalState): ItemLoadingState => {
  return {
    isVisible: state.isVisible,
    progress: state.currentProgress,
    langTextKeys: state.loadingOpts?.langTextKeys,
    progressType: state.loadingOpts?.progress?.type,
    reason: state.loadingOpts?.reason,
  };
};

const applyItemLoadingState = (item: Item, state: ItemLoadingInternalState | null): void => {
  const onCanvasLoader = state?.loadingOpts?.onCanvasLoader;
  if (state?.isVisible && onCanvasLoader) {
    item.loadingAnimation.start(onCanvasLoader);
  } else {
    item.loadingAnimation.stop();
  }

  window.PMW.redux.store.dispatch(
    updateItemLoadingState({
      pageHashedId: item.page.hashedID,
      itemId: item.uid,
      state: state ? toReduxLoadingState(state) : null,
    })
  );
};

const onProgressUpdate = (item: Item, progress: number, hasTimedOut: boolean): void => {
  const state = getItemState(item);
  if (!state) {
    return;
  }

  if (hasTimedOut) {
    removeItemLoading(item);
    return;
  }

  state.currentProgress = progress;
  applyItemLoadingState(item, state);
};

const startFakeLoadingCounter = (item: Item, state: ItemLoadingInternalState): void => {
  if (state.loadingOpts?.progress?.type === ItemLoadingProgressType.FAKE) {
    state.progressInterval = initFakeProgress(
      state.loadingOpts.progress.expectedTotalTime * 1000,
      (progress: number, hasTimedOut: boolean) => {
        onProgressUpdate(item, progress, hasTimedOut);
      },
      state.currentProgress
    );
  }
};

const resetItemLoadingState = (item: Item): void => {
  const state = getItemState(item);
  if (state?.progressInterval) {
    clearInterval(state.progressInterval);
  }
  removeItemState(item);
};

/**
 * Start loading for an item
 */
export const startItemLoading = (item: Item, opts?: ShowItemLoadingOtps): void => {
  resetItemLoadingState(item);

  const state: ItemLoadingInternalState = {
    currentProgress: 0,
    loadingOpts: opts,
    isVisible: true,
  };

  setItemState(item, state);

  if (opts?.progress?.type === ItemLoadingProgressType.FAKE) {
    startFakeLoadingCounter(item, state);
  }

  applyItemLoadingState(item, state);
};

/**
 * Remove loading for an item
 */
export const removeItemLoading = (item: Item): void => {
  resetItemLoadingState(item);
  applyItemLoadingState(item, null);
};

/**
 * Show loading (make it visible)
 */
export const showItemLoading = (item: Item): void => {
  const state = getItemState(item);
  if (!state) {
    return;
  }

  state.isVisible = true;
  applyItemLoadingState(item, state);
};

/**
 * Hide loading (make it invisible but keep state)
 */
export const hideItemLoading = (item: Item): void => {
  const state = getItemState(item);
  if (!state) {
    return;
  }

  state.isVisible = false;
  applyItemLoadingState(item, state);
};

/**
 * Transfer loading state from one item to another (when moving items between pages)
 */
export const transferItemLoadingStates = (oldItem: Item, newItem: Item): void => {
  const oldState = getItemState(oldItem);
  if (oldState?.loadingOpts && oldState.currentProgress < 1.0) {
    resetItemLoadingState(newItem);

    // Calculate remaining time
    let adjustedLoadingOpts = oldState.loadingOpts;
    if (oldState.loadingOpts.progress?.type === ItemLoadingProgressType.FAKE && oldState.loadingOpts.progress.expectedTotalTime) {
      const remainingTime = oldState.loadingOpts.progress.expectedTotalTime * (1 - oldState.currentProgress);
      adjustedLoadingOpts = {
        ...oldState.loadingOpts,
        progress: {
          ...oldState.loadingOpts.progress,
          expectedTotalTime: remainingTime,
        },
      };
    }

    const newState: ItemLoadingInternalState = {
      currentProgress: oldState.currentProgress,
      loadingOpts: adjustedLoadingOpts,
      isVisible: oldState.isVisible,
    };

    setItemState(newItem, newState);

    if (adjustedLoadingOpts?.progress?.type === ItemLoadingProgressType.FAKE) {
      startFakeLoadingCounter(newItem, newState);
    }

    applyItemLoadingState(newItem, newState);
  }

  if (oldItem.isSlideshow() && newItem.isSlideshow()) {
    const oldSlidesOrder = oldItem.slides.slidesOrder;
    const newSlidesOrder = newItem.slides.slidesOrder;

    for (let i = 0; i < oldSlidesOrder.length && i < newSlidesOrder.length; i++) {
      const oldSlideUid = oldSlidesOrder[i];
      const newSlideUid = newSlidesOrder[i];

      const oldSlide = oldItem.slides.slidesHashMap[oldSlideUid];
      const newSlide = newItem.slides.slidesHashMap[newSlideUid];

      if (oldSlide && newSlide) {
        transferItemLoadingStates(oldSlide, newSlide);
      }
    }
  }
};
